import { FlaskConical, Inbox } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { MenuItem, MenuSeparator } from '../../../ui/Menu';
import { Section } from '../../../ui/Section';
import { EmptyState } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { ModulePage } from '../../modules/pages/ModulePage';
import { InboxDetail } from '../components/InboxDetail';
import { InboxList } from '../components/InboxList';
import { canSimulateEvents, SimulateEventDialog } from '../components/SimulateEventDialog';

/**
 * Inbox (/inbox, /inbox/:id; Figma 02 desktop i mobile): na desktopie lista 360 px po lewej i podgląd po prawej
 * (kolumna 640 px), na mobile osobno lista albo podgląd (z powrotem do listy). Filtry w adresie zostają przy
 * przechodzeniu między pozycjami. W trybie deweloperskim owner/admin ma w menu „…” „Symuluj zdarzenie”.
 */
export function InboxPage() {
  const access = useAccess();
  if (!access.can('inbox')) return <ModulePage module="inbox" />;
  return <InboxWorkspace />;
}

/**
 * Adres z powiadomienia push (`/inbox?zdarzenie=<eventId>`, `pushMessageFor`): otwiera moją pozycję tego zdarzenia
 * (`inbox.itemForEvent`), a parametr znika z adresu. Brak pozycji (zdarzenie poza zakresem, cudze) — lista i komunikat.
 */
function useEventDeepLink(selectedId: string | undefined) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const eventId = params.get('zdarzenie');
  const enabled = Boolean(eventId) && !selectedId;
  const query = trpc.inbox.itemForEvent.useQuery({ eventId: eventId ?? '' }, { enabled, retry: false });
  useEffect(() => {
    if (!enabled || query.isPending) return;
    const next = new URLSearchParams(params);
    next.delete('zdarzenie');
    const qs = next.toString() ? `?${next.toString()}` : '';
    const itemId = query.data?.id;
    if (itemId) {
      navigate(`/inbox/${itemId}${qs}`, { replace: true });
    } else {
      toast('Tej pozycji nie ma w Twoim Inboxie.');
      navigate(`/inbox${qs}`, { replace: true });
    }
  }, [enabled, query.isPending, query.data, params, navigate]);
}

function InboxWorkspace() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const access = useAccess();
  const [simulateOpen, setSimulateOpen] = useState(false);
  const simulate = canSimulateEvents(access);
  useEventDeepLink(id);
  const search = params.toString();

  return (
    <div className="md:flex md:min-h-dvh">
      <div
        className={cn(
          // desktop: lista w białej kolumnie (jak menu boczne), podgląd na szarym tle w białym bloku
          'px-4 pt-6 pb-8 md:sticky md:top-0 md:h-dvh md:w-[360px] md:shrink-0 md:overflow-y-auto md:border-r md:border-line md:bg-panel md:px-4 md:pt-7',
          id && 'hidden md:block',
        )}
      >
        <InboxList
          selectedId={id ?? null}
          menu={
            simulate ? (
              <>
                <MenuSeparator />
                <MenuItem icon={FlaskConical} onSelect={() => setSimulateOpen(true)}>
                  Symuluj zdarzenie…
                </MenuItem>
              </>
            ) : null
          }
        />
      </div>
      <div className={cn('min-w-0 flex-1', !id && 'hidden md:block')}>
        <div className="mx-auto w-full max-w-[704px] px-4 pt-6 pb-16 md:px-8 md:pt-14">
          {id ? (
            <InboxDetail key={id} id={id} backTo={`/inbox${search ? `?${search}` : ''}`} />
          ) : (
            <Section>
              <EmptyState
                icon={Inbox}
                title="Wybierz wiadomość z listy"
                description="Podgląd pokaże treść, powiązania i akcje: odpowiedź, zadanie, zrobione, odłożenie."
                className="py-24"
              />
            </Section>
          )}
        </div>
      </div>
      {simulate && <SimulateEventDialog open={simulateOpen} onOpenChange={setSimulateOpen} />}
    </div>
  );
}
